import * as React from "react";
import { Button, Field, Input, Text, makeStyles } from "@fluentui/react-components";
import { Add24Regular, Subtract24Regular } from "@fluentui/react-icons";
import type { FetchXmlBuilderTree, IFetchXmlBuilderDataSource, QueryExecutionResult } from "./types.js";
import { QueryDiagnosticsSection } from "./components/sections/QueryDiagnosticsSection.js";

export interface QueryTesterControlProps {
  dataSource: IFetchXmlBuilderDataSource | null;
  tree: FetchXmlBuilderTree | null;
}

const useStyles = makeStyles({
  card: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gap: "8px",
  },
  actionsRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  settingsGrid: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  settingField: {
    minWidth: "0",
  },
  errorText: {
    color: "var(--colorPaletteRedForeground1)",
  },
  settingsHeader: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

export function QueryTesterControl({ dataSource, tree }: QueryTesterControlProps): JSX.Element {
  const styles = useStyles();
  const [runResult, setRunResult] = React.useState<QueryExecutionResult | null>(null);
  const [runError, setRunError] = React.useState("");
  const [runLoading, setRunLoading] = React.useState(false);
  const [hierarchicalMode, setHierarchicalMode] = React.useState(false);
  const [hierarchicalSettingsExpanded, setHierarchicalSettingsExpanded] = React.useState(false);
  const [rootBatchSizeText, setRootBatchSizeText] = React.useState("500");
  const [rootPageLimitText, setRootPageLimitText] = React.useState("1");
  const [inBatchSizeText, setInBatchSizeText] = React.useState("1000");
  const [relationPageLimitText, setRelationPageLimitText] = React.useState("25");
  const [relationBatchConcurrencyText, setRelationBatchConcurrencyText] = React.useState("3");

  const canRunHierarchical = Boolean(dataSource?.executeQueryHierarchical);

  const parsePositiveInteger = React.useCallback((value: string): number | undefined => {
    const parsed = Number.parseInt(value.trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return undefined;
    }

    return parsed;
  }, []);

  React.useEffect(() => {
    if (!hierarchicalMode) {
      setHierarchicalSettingsExpanded(false);
    }
  }, [hierarchicalMode]);

  const onRunQuery = React.useCallback(async () => {
    if (!dataSource || !tree) {
      return;
    }

    setRunLoading(true);
    setRunError("");
    setRunResult(null);

    try {
      const result =
        hierarchicalMode && dataSource.executeQueryHierarchical
            ? await dataSource.executeQueryHierarchical(tree, {
                rootBatchSize: parsePositiveInteger(rootBatchSizeText),
                rootPageLimit: parsePositiveInteger(rootPageLimitText),
                inBatchSize: parsePositiveInteger(inBatchSizeText),
                relationPageLimit: parsePositiveInteger(relationPageLimitText),
                relationBatchConcurrency: parsePositiveInteger(relationBatchConcurrencyText),
              })
          : await dataSource.executeQuery(tree);
      setRunResult(result);
    } catch (error) {
      setRunError(String(error));
    } finally {
      setRunLoading(false);
    }
  }, [
    dataSource,
    hierarchicalMode,
    inBatchSizeText,
    parsePositiveInteger,
    relationBatchConcurrencyText,
    relationPageLimitText,
    rootBatchSizeText,
    rootPageLimitText,
    tree,
  ]);

  return (
    <>
      <div className={styles.card}>
        <Text weight="semibold">Query Tester</Text>
        <div className={styles.actionsRow}>
          {canRunHierarchical ? (
            <Button appearance={hierarchicalMode ? "primary" : "secondary"} onClick={() => setHierarchicalMode((current) => !current)}>
              Hierarchical mode: {hierarchicalMode ? "On" : "Off"}
            </Button>
          ) : null}
          <Button appearance="primary" onClick={onRunQuery} disabled={!dataSource || !tree || runLoading}>
            {runLoading ? "Running..." : hierarchicalMode ? "Run hierarchical query" : "Run query"}
          </Button>
        </div>

        {canRunHierarchical && hierarchicalMode ? (
          <div className={styles.card}>
            <div className={styles.settingsHeader}>
              <Text weight="semibold">Hierarchical parameters</Text>
              <Button
                appearance="subtle"
                icon={hierarchicalSettingsExpanded ? <Subtract24Regular /> : <Add24Regular />}
                aria-label={hierarchicalSettingsExpanded ? "Collapse hierarchical parameters" : "Expand hierarchical parameters"}
                title={hierarchicalSettingsExpanded ? "Collapse" : "Expand"}
                onClick={() => setHierarchicalSettingsExpanded((current) => !current)}
              />
            </div>

            {hierarchicalSettingsExpanded ? (
              <div className={styles.settingsGrid}>
                <Field className={styles.settingField} label="Root batch" orientation="vertical">
                  <Input value={rootBatchSizeText} onChange={(_event, data) => setRootBatchSizeText(data.value)} />
                </Field>
                <Field className={styles.settingField} label="Root pages" orientation="vertical">
                  <Input value={rootPageLimitText} onChange={(_event, data) => setRootPageLimitText(data.value)} />
                </Field>
                <Field className={styles.settingField} label="IN batch" orientation="vertical">
                  <Input value={inBatchSizeText} onChange={(_event, data) => setInBatchSizeText(data.value)} />
                </Field>
                <Field className={styles.settingField} label="Relation pages" orientation="vertical">
                  <Input value={relationPageLimitText} onChange={(_event, data) => setRelationPageLimitText(data.value)} />
                </Field>
                <Field className={styles.settingField} label="Relation concurrency" orientation="vertical">
                  <Input value={relationBatchConcurrencyText} onChange={(_event, data) => setRelationBatchConcurrencyText(data.value)} />
                </Field>
              </div>
            ) : null}
          </div>
        ) : null}

        {runError ? <Text className={styles.errorText}>{runError}</Text> : null}
      </div>
      {runResult ? <QueryDiagnosticsSection runResult={runResult} /> : null}
    </>
  );
}
